import { useState, useEffect } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "../AuthContext";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Employee Portal | RUDRA EMPIRE";
  }, []);

  if (user) return <Navigate to="/portal/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back.");
      navigate("/portal/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <main
      data-testid="portal-login-page"
      className="min-h-screen bg-[#08080A] text-neutral-100 grid grid-cols-1 md:grid-cols-2"
    >
      {/* Left brand panel */}
      <div className="relative hidden md:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1652820330042-92a30b18abc5?crop=entropy&cs=srgb&fm=jpg&w=1400&q=70"
          alt="Karnataka land"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#08080A] via-[#08080A]/70 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <img
            src="/crest.png"
            alt="Rudra Empire"
            className="h-16 w-auto self-start"
          />
          <div>
            <div className="font-display italic text-3xl text-[#D4AF37] mb-3">
              Welcome to the World of Business.
            </div>
            <div className="text-neutral-400 text-sm tracking-widest uppercase">
              Employee Operations Portal · Est. 1995
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-8 md:p-16 relative">
        <Link
          to="/"
          data-testid="portal-back-home"
          className="absolute top-6 left-6 text-xs tracking-[0.24em] uppercase text-neutral-500 hover:text-[#D4AF37] flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Public Site
        </Link>

        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 border border-[#D4AF37]/50 flex items-center justify-center">
              <Lock strokeWidth={1} className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <div className="overline">Rudra Empire · Portal</div>
              <div className="font-display text-3xl text-neutral-50 leading-none mt-1">
                Sign in
              </div>
            </div>
          </div>

          <form onSubmit={submit} data-testid="portal-login-form" className="space-y-6">
            <div>
              <label className="overline block mb-3" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                data-testid="portal-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-white/15 py-3 text-neutral-100 focus:outline-none focus:border-[#D4AF37] transition-colors placeholder:text-neutral-600"
                placeholder="you@rudraempire.com"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="overline block mb-3" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                data-testid="portal-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border-b border-white/15 py-3 text-neutral-100 focus:outline-none focus:border-[#D4AF37] transition-colors placeholder:text-neutral-600"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              data-testid="portal-login-submit"
              disabled={loading}
              className="mt-4 w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#08080A] px-8 py-4 text-xs tracking-[0.24em] uppercase font-semibold hover:bg-[#F0D88B] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Authenticating
                </>
              ) : (
                <>Sign in →</>
              )}
            </button>
          </form>

          <p className="mt-10 text-xs text-neutral-500 leading-relaxed">
            Access is granted by the Rudra Empire admin. Contact your administrator to
            request an account. All sessions are logged.
          </p>
        </div>
      </div>
    </main>
  );
}
